'use client';

import { LoadingPage } from '@/components/core/loading-page';
import { useSiteStore } from '@/store/site';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { convertApiLocale } from '@nf/utils-common/site';
import { useQuery } from '@tanstack/react-query';
import type { AbstractIntlMessages } from 'next-intl';
import { NextIntlClientProvider } from 'next-intl';
import type { ReactNode } from 'react';

interface NextIntlProviderProps {
	locale: string;
	children: ReactNode;
}

function NextIntlProvider({ locale, children }: NextIntlProviderProps) {
	const [apiDomain] = useSiteStore(state => [state.apiDomain]);
	const fileVersionId = process.env.NEXT_PUBLIC_FILE_VERSION_ID ?? '';
	const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
	const ctcdDomain = apiDomain?.ctcdDomain ?? '';

	const { data: messages } = useQuery({
		queryKey: ['resourceData', locale, fileVersionId],
		enabled: Boolean(ctcdDomain && hostname),
		staleTime: Infinity,
		cacheTime: Infinity,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		retry: 5,
		retryDelay: 1000,
		queryFn: async ({ signal }) => {
			const apiUrl = new URL(`${ctcdDomain}/api/Resource/GetResourceData`);
			apiUrl.searchParams.set('lang', convertApiLocale(locale));
			apiUrl.searchParams.set('skinMode', '7'); // Mars也會拿Galaxy的Resource
			apiUrl.searchParams.set('domain', hostname);
			if (fileVersionId) {
				apiUrl.searchParams.set('v', fileVersionId);
			}

			const response = await fetchComposer.get(String(apiUrl), { signal });
			if (!response.ok) {
				throw new Error(`${response.status}: ${response.statusText}`);
			}

			const json = await response.json();
			const data = (json?.Data ?? null) as AbstractIntlMessages | null;
			return (data ?? {}) as AbstractIntlMessages;
		},
	});

	return (
		<NextIntlClientProvider
			locale={locale}
			messages={(messages ?? {}) as AbstractIntlMessages}
		>
			{messages ? children : <LoadingPage />}
		</NextIntlClientProvider>
	);
}

export default NextIntlProvider;